import { Genders } from '@chiroup/core/enums/Genders.enum';
import { RequestStep } from '@chiroup/core/types/Request.type';
import {
  SurveyDraft,
  SurveyDraftInput,
  SurveyQuestion,
  SurveyQuestionAnswer,
} from '@chiroup/core/types/Survey.type';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '../Alert';
import { SoftSurveyQuestion } from '../survey/soft';
import Body from './Body';
import CCSInstructions from './CCSInstructions';
import { bodyPickerSubmit, getNextQuestionIndex, submitStuff } from './helpers';
import { recoverStaleSurveySession as recoverStaleSurveySessionShared } from './recoverStaleSurveySession';

const submitVerbiage = {
  more: {
    en: 'Hang tight, we have a few more things we need to ask...',
    es: 'Espera un momento, tenemos algunas cosas más que debemos preguntar...',
  },
  done: { en: 'Submitting survey', es: 'La encuesta esta siendo enviada' },
};

function serializeUnknownError(e: unknown) {
  const a = e as any;

  return {
    name: a?.name,
    message: a?.message ?? String(a),
    stack: a?.stack,
    code: a?.code,
    status: a?.response?.status,
    responseData: a?.response?.data,
    cause: a?.cause,
  };
}

function toError(e: unknown): Error {
  if (e instanceof Error) return e;
  const a = e as any;
  // ChiroUpAPI errors are structured as { response: { data: { message, statusCode, ... } } }
  // Check for message in nested path first, then fallback to direct message property
  const msg =
    a?.response?.data?.message ?? a?.message ?? (typeof a === 'string' ? a : 'Unknown error');
  const err = new Error(msg);
  // carry over useful props so Sentry and logs have details
  (err as any).code = a?.code;
  (err as any).response = a?.response;
  (err as any).cause = a?.cause;

  return err;
}

type Props = {
  myScreen?: boolean;
  setQuestionChanging?: React.Dispatch<React.SetStateAction<boolean>>;
  primaryColor: string;
  language: 'en' | 'es' | undefined;
  gender: Genders;
  age: number;
  complete: () => void;
  ccsFollowups?: string[];
  kiosk: boolean;
  prMap: { [key: string]: string };
  markSurveyComplete: () => void;
  onAccsChainExtend?: () => void;
  updateRequest?: (
    data: {
      prMap: { [key: string]: string };
      steps: RequestStep[];
      language: 'en' | 'es';
      gender: Genders;
      age: number;
      id: string;
    },
    callback?: () => void
  ) => void;
  validationCode: string | number;
  surveyIds?: string[];
  startQuestion: SurveyQuestion;
  submitSurvey: (
    questions: SurveyQuestion[],
    kiosk: boolean,
    spanishQuestions?: SurveyQuestion[],
    ccsFollowupSurveyIds?: string[],
    validationCode?: string | number
  ) => Promise<any>;
  startingProblemArea?: number;
  additionalComplaints?: boolean;
  abbreviatedCCS?: boolean;
  doNotAllowUnrelatedRegions?: boolean;
  customQuestions?: SurveyQuestion[];
  allowAdditionalComplaints?: boolean;
  customCCSSurveyId?: string;
  currentSurveyId?: string;
  draft?: SurveyDraft;
  saveDraft?: (draft: SurveyDraftInput) => Promise<void>;
};

export const ChiefComplaintSurvey = ({
  primaryColor,
  language,
  gender,
  age,
  complete,
  ccsFollowups,
  markSurveyComplete,
  onAccsChainExtend,
  updateRequest,
  validationCode,
  surveyIds,
  startQuestion,
  submitSurvey,
  kiosk,
  myScreen = false,
  setQuestionChanging,
  startingProblemArea,
  additionalComplaints = false,
  abbreviatedCCS = false,
  doNotAllowUnrelatedRegions = false,
  customQuestions,
  allowAdditionalComplaints = true,
  customCCSSurveyId,
  currentSurveyId,
  draft,
  saveDraft,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [spanishQuestions, setSpanishQuestions] = useState<SurveyQuestion[]>([startQuestion]);
  const [currentQuestion, setCurrentQuestion] = useState<any>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [submitText, setSubmitText] = useState<string>(
    submitVerbiage.done[language === 'es' ? 'es' : 'en']
  );
  const [disabled, setDisabled] = useState(false);
  const [show, setShow] = useState<boolean>(true);
  const [aaWI, setAaWI] = useState<string>();
  const [hasError, setHasError] = useState(false);
  const [instructions, setInstructions] = useState<boolean>(true);
  const modalHeight = useRef(0);
  const questionsRef = useRef<SurveyQuestion[]>([]);
  const spanishQuestionsRef = useRef<SurveyQuestion[]>([startQuestion]);
  const draftSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const lastQueuedDraftRef = useRef(
    draft
      ? JSON.stringify({
          questions: draft.questions,
          spanishQuestions: draft.spanishQuestions,
        })
      : ''
  );

  const persistDraft = useCallback(
    (
      nextQuestions: SurveyQuestion[],
      nextSpanishQuestions: SurveyQuestion[],
      force = false
    ): Promise<void> => {
      if (!saveDraft || !nextQuestions.some(question => !!question?.answer)) {
        return draftSaveQueueRef.current;
      }

      const nextDraft: SurveyDraftInput = {
        questions: nextQuestions,
        spanishQuestions: nextSpanishQuestions,
      };
      const serialized = JSON.stringify(nextDraft);

      if (!force && serialized === lastQueuedDraftRef.current) {
        return draftSaveQueueRef.current;
      }

      lastQueuedDraftRef.current = serialized;
      const answeredCount = nextQuestions.filter(question => !!question?.answer).length;

      draftSaveQueueRef.current = draftSaveQueueRef.current
        .then(() => saveDraft(nextDraft))
        .then(() => {
          Sentry.logger.info('Chief complaint survey draft saved', {
            kiosk,
            answeredCount,
          });
        })
        .catch(err => {
          if (lastQueuedDraftRef.current === serialized) {
            lastQueuedDraftRef.current = '';
          }

          Sentry.withScope(scope => {
            scope.setTag('surveyDraftSave', 'failed');
            scope.setExtra('kiosk', kiosk);
            scope.setExtra('answeredCount', answeredCount);
            Sentry.captureException(toError(err));
          });
        });

      return draftSaveQueueRef.current;
    },
    [kiosk, saveDraft]
  );

  const recoverStaleSurveySession = (err: unknown) =>
    recoverStaleSurveySessionShared({
      err,
      kiosk,
      language,
      currentQuestionId: currentQuestion?.id,
    });

  const progress = useMemo(() => {
    const questionsCopy = [...questions];
    const total = questionsCopy?.length;
    const reverseIndexOfLastAnswer = questionsCopy.reverse().findIndex(q => !!q.answer);
    const reverseIndexOfLastAnswerOrZero =
      reverseIndexOfLastAnswer > -1 ? reverseIndexOfLastAnswer : questionsCopy.length;
    const completed = questionsCopy.length - reverseIndexOfLastAnswerOrZero;

    return {
      completed: completed || 0,
      total: total || 1,
      // CCS-family surveys branch on body-region selection, so the total
      // grows as follow-ups are appended — a jumping "12 / 67" count upsets
      // patients. Show the bar only (Monday 12560530217).
      hideCount: true,
    };
  }, [questions]);

  const disableSurvey = async () => {
    return new Promise((resolve, reject) => {
      setDisabled(true);
      setShow(false);
      setQuestionChanging?.(true);
      // Wait 333ms for the question to disappear
      setTimeout(() => {
        resolve(true);
      }, 333);
    });
  };

  const enableSurvey = async () => {
    return new Promise((resolve, reject) => {
      // Wait 333ms for the question to have been changed
      setTimeout(() => {
        setDisabled(false);
        setShow(true);
        setIsSubmitting(false);
        setQuestionChanging?.(false);
        resolve(true);
      }, 333);
    });
  };

  const onSelect = async (val: SurveyQuestionAnswer) => {
    setHasError(false);
    const height = document.getElementById('survey-modal');

    if (height) {
      modalHeight.current = height.clientHeight;
    }

    await disableSurvey();

    if (
      currentQuestion?.id === '5f2cb48c-f08d-4e14-abb2-42d2eae74a83' &&
      val.value === '1' &&
      myScreen
    ) {
      return submitStuff({
        questions,
        kiosk,
        spanishQuestions,
        setIsSubmitting,
        isSubmitting,
        submitSurvey,
        complete,
        markSurveyComplete,
        onAccsChainExtend,
        setSubmitText,
        submitVerbiage,
        validationCode,
        language,
        surveyIds,
        ccsFollowups,
        customCCSSurveyId,
        currentSurveyId,
        updateRequest,
      }).catch(err => {
        if (recoverStaleSurveySession(err)) return;

        const serialized = serializeUnknownError(err);
        console.error('ChiefComplaintSurvey error:', serialized, err);
        Sentry.logger.error('Chief complaint survey submit failed', {
          kiosk,
          status: serialized.status || null,
        });

        Sentry.withScope(scope => {
          scope.setExtra('serialized', serialized);

          try {
            scope.setExtra('serializedError', JSON.stringify(err));
          } catch {
            scope.setExtra('serializedError', '[stringify-failed]');
          }

          scope.setExtra('errorString', String(err));
          scope.setExtra('errorName', (err as any)?.name);
          scope.setExtra('errorMessage', (err as any)?.message);
          scope.setExtra('errorStack', (err as any)?.stack);
          Sentry.captureException(toError(err));
        });
        setCurrentQuestion((prev: any) => {
          return { ...prev, answer: null };
        });
        setHasError(true);
        enableSurvey();
      });
    }

    if (
      val.value === (language === 'es' ? 'Accidente automovilístico' : 'Auto accident') ||
      val.value ===
        (language === 'es' ? 'Lesión relacionada con el empleo' : 'Employment-related injury')
    ) {
      setAaWI(val.value);
    }

    const currentQuestionIndex = questions.findIndex(
      (q: { id: string }) => q.id === currentQuestion.id
    );
    const newQuestions = [...questions];
    const newQuestionsSpanish = spanishQuestions ? [...spanishQuestions] : [];
    newQuestions[currentQuestionIndex].answer = val;

    if (language === 'es') {
      newQuestionsSpanish[currentQuestionIndex].answer = val;
    }

    const nextQuestionIndex = getNextQuestionIndex({ questions: newQuestions });
    const nextQuestion = ((language === 'es' ? newQuestionsSpanish : newQuestions) || [])?.[
      nextQuestionIndex
    ];

    if (!nextQuestion) {
      setQuestions(newQuestions);

      if (language === 'es') {
        setSpanishQuestions(newQuestionsSpanish);
      }

      await persistDraft(newQuestions, newQuestionsSpanish, true);
      setDisabled(false);

      return submitStuff({
        questions: newQuestions,
        kiosk,
        spanishQuestions: newQuestionsSpanish,
        setIsSubmitting,
        isSubmitting,
        submitSurvey,
        complete,
        markSurveyComplete,
        onAccsChainExtend,
        setSubmitText,
        submitVerbiage,
        validationCode,
        language,
        surveyIds,
        ccsFollowups,
        customCCSSurveyId,
        currentSurveyId,
        updateRequest,
      }).catch(err => {
        if (recoverStaleSurveySession(err)) return;

        const serialized = serializeUnknownError(err);
        console.error('ChiefComplaintSurvey error:', serialized, err);
        Sentry.logger.error('Chief complaint survey submit failed', {
          kiosk,
          status: serialized.status || null,
        });

        Sentry.withScope(scope => {
          scope.setExtra('serialized', serialized);

          try {
            scope.setExtra('serializedError', JSON.stringify(err));
          } catch {
            scope.setExtra('serializedError', '[stringify-failed]');
          }

          scope.setExtra('errorString', String(err));
          scope.setExtra('errorName', (err as any)?.name);
          scope.setExtra('errorMessage', (err as any)?.message);
          scope.setExtra('errorStack', (err as any)?.stack);
          Sentry.captureException(toError(err));
        });

        enableSurvey();
        setCurrentQuestion((prev: any) => {
          return { ...prev, answer: null };
        });
        setHasError(true);
      });
    }

    setQuestions(newQuestions);
    setQuestionChanging?.(true);

    if (setSpanishQuestions && language === 'es') {
      setSpanishQuestions(newQuestionsSpanish);
    }

    // Resolve `notLessThan` / `notMoreThan` references — the field is
    // declared as a questionId string in the question template (see
    // ccsQuestions.ts: `notLessThan: intensityQuestionId`). Resolve
    // onto a render-time clone so the array entry keeps the template
    // string. If the user later goes back and changes the referenced
    // answer, the next forward pass re-resolves against the fresh
    // value instead of being stuck with whatever was current the
    // first time through.
    let renderedNextQuestion = nextQuestion;
    const nextOpts = nextQuestion?.typeOptions;
    const hasNotLessThanRef = typeof nextOpts?.notLessThan === 'string';
    const hasNotMoreThanRef = typeof nextOpts?.notMoreThan === 'string';

    if (hasNotLessThanRef || hasNotMoreThanRef) {
      const resolvedTypeOptions = { ...nextOpts };
      if (typeof resolvedTypeOptions.notLessThan === 'string') {
        resolvedTypeOptions.notLessThan = getValueOfQuestion(resolvedTypeOptions.notLessThan);
      }
      if (typeof resolvedTypeOptions.notMoreThan === 'string') {
        resolvedTypeOptions.notMoreThan = getValueOfQuestion(resolvedTypeOptions.notMoreThan);
      }
      renderedNextQuestion = { ...nextQuestion, typeOptions: resolvedTypeOptions };
    }

    setCurrentQuestion(renderedNextQuestion);

    await enableSurvey();

    return;
  };

  const getValueOfQuestion = (questionId: string) => {
    const question = questions.find((question: { id: string }) => question.id === questionId);

    if (question) {
      return question.answer?.value;
    }
  };

  useEffect(() => {
    const restoredQuestions = (draft?.questions || []).filter(Boolean);
    const restoredSpanishQuestions = (draft?.spanishQuestions || []).filter(Boolean);
    const nextQuestions = restoredQuestions.length ? restoredQuestions : [startQuestion];
    const nextSpanishQuestions = restoredSpanishQuestions.length
      ? restoredSpanishQuestions
      : [startQuestion];
    const displayedQuestions = language === 'es' ? nextSpanishQuestions : nextQuestions;
    const resumeIndex = getNextQuestionIndex({ questions: nextQuestions, age, gender });
    const unansweredQuestion = resumeIndex >= 0 ? displayedQuestions[resumeIndex] : undefined;
    const fallbackQuestion = displayedQuestions[displayedQuestions.length - 1] || startQuestion;

    setQuestions(nextQuestions);
    setSpanishQuestions(nextSpanishQuestions);
    setCurrentQuestion(
      unansweredQuestion || {
        ...fallbackQuestion,
        answer: undefined,
      }
    );

    if (restoredQuestions.some(question => !!question.answer)) {
      setInstructions(false);
    }
  }, [age, draft, gender, language, startQuestion]);

  useEffect(() => {
    questionsRef.current = questions;
    spanishQuestionsRef.current = spanishQuestions;
    void persistDraft(questions, spanishQuestions);
  }, [persistDraft, questions, spanishQuestions]);

  useEffect(() => {
    const flushDraft = () => {
      void persistDraft(questionsRef.current, spanishQuestionsRef.current, true);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushDraft();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', flushDraft);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', flushDraft);
    };
  }, [persistDraft]);

  const goBack = async () => {
    await disableSurvey();
    const currentQuestionIndex = questions.findIndex(
      (q: { id: string }) => q.id === currentQuestion.id
    );
    const mostRecentQuestionThatWasAnswered = questions
      .slice(0, currentQuestionIndex)
      .reverse()
      .find(q => q.answer);

    if (!mostRecentQuestionThatWasAnswered) return;

    const mostRecentQuestionThatWasAnsweredIndex = questions.findIndex(
      q => q.id === mostRecentQuestionThatWasAnswered?.id
    );

    if (mostRecentQuestionThatWasAnswered?.type === 'bodypicker') {
      const thisProblemArea = mostRecentQuestionThatWasAnswered.problemArea;
      setQuestions(prev => {
        const questionsBeforeIndex = prev.slice(0, mostRecentQuestionThatWasAnsweredIndex + 1);

        return questionsBeforeIndex.filter(
          q => q.problemArea !== thisProblemArea || q.type === 'bodypicker'
        );
      });
    } else {
      setQuestions(prev => {
        return prev.map((q, i) => {
          if (i > mostRecentQuestionThatWasAnsweredIndex) {
            return { ...q, answer: undefined };
          }

          return q;
        });
      });
    }

    setCurrentQuestion({ ...mostRecentQuestionThatWasAnswered, answer: null });
    await enableSurvey();
  };

  return instructions && !myScreen ? (
    <CCSInstructions
      setInstructions={setInstructions}
      language={language}
      primaryColor={primaryColor}
    />
  ) : (
    <div className="flex flex-col gap-4">
      {!!hasError && show && (
        <Alert title="Error" error="There was an error submitting your survey. Please try again." />
      )}
      {currentQuestion &&
        (currentQuestion.type === 'bodypicker' ? (
          <Body
            show={show}
            bodyPickerSubmit={bodyPickerSubmit}
            language={language}
            id={currentQuestion.id}
            primaryColor={primaryColor}
            spanishQuestions={spanishQuestions}
            questions={questions}
            currentQuestion={currentQuestion}
            setSpanishQuestions={setSpanishQuestions}
            setQuestions={setQuestions}
            setCurrentQuestion={setCurrentQuestion}
            aaWI={aaWI}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            complete={complete}
            markSurveyComplete={markSurveyComplete}
            submitSurvey={submitSurvey}
            submitVerbiage={submitVerbiage}
            setSubmitText={setSubmitText}
            validationCode={validationCode}
            surveyIds={surveyIds}
            ccsFollowups={ccsFollowups}
            updateRequest={updateRequest}
            kiosk={kiosk}
            myScreen={myScreen}
            startingProblemArea={startingProblemArea}
            additionalComplaints={additionalComplaints}
            abbreviatedCCS={abbreviatedCCS}
            doNotAllowUnrelatedRegions={doNotAllowUnrelatedRegions}
            goBack={goBack}
            customQuestions={customQuestions}
            allowAdditionalComplaints={allowAdditionalComplaints}
            saveDraft={persistDraft}
          />
        ) : (
          // Non-bodypicker questions (radio / checkbox / slider /
          // date / textarea / etc.) render through the shared soft
          // card UI so CCS surveys match the rest of the new portal
          // flow. The legacy `SurveyQuestionComponent` is no longer
          // referenced here; staff-facing surveys (chiroup admin,
          // legacy healthcom-app) keep their own callers.
          <SoftSurveyQuestion
            // `key` forces a remount when the question changes so each
            // question's internal state (e.g. SoftSliderQuestion's
            // `value`) starts fresh. Without this, advancing from a
            // max=100 slider to a max=10 slider visually clamps the
            // thumb but the displayed numeric value carries over from
            // the previous question.
            key={currentQuestion.id}
            question={currentQuestion}
            language={language === 'es' ? 'es' : 'en'}
            primaryColor={primaryColor}
            disabled={disabled || !!currentQuestion.answer}
            hidden={!show}
            showPrevious
            onBack={goBack}
            onSelect={onSelect}
            progress={progress}
          />
        ))}
    </div>
  );
};
